import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Grid,
  Button,
  IconButton
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Fingerprint as FingerprintIcon,
  Link as LinkIcon,
  AccessTime as AccessTimeIcon,
  Chat as ChatIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { useUser } from '../../../contexts/UserContext';

const formatDate = (date) => {
  if (!date) return 'N/A';
  const dateObj = new Date(date);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[dateObj.getMonth()];
  const day = dateObj.getDate();
  let hours = dateObj.getHours();
  const minutes = dateObj.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  return `${month} ${day}, ${hours}:${minutes} ${ampm}`;
};

const AgentCard = ({ agent, onDelete }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { onlyServiceRoleUser } = useUser();

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(agent);
  };

  const handleManageAgent = () => {
    navigate(`/agents/${agent.id}/manage`);
  };

  const handleStartPioAgent = () => {
    const apiKey = agent.apiKey || agent.api_key || (agent.agent && agent.agent.apiKey) || '';
    const endpoint = agent.endpoint || (agent.agent && agent.agent.endpoint) || '';
    const agentId = agent.id || (agent.agent && agent.agent.id) || '';
    const agentName = agent.name || (agent.agent && agent.agent.name) || '';

    if (!apiKey || !endpoint) {
      console.error('Missing API key or endpoint for agent. Please ensure the agent is fetched via getMyAgents or getAgents API:', agent);
      return;
    }

    const agentInfo = {
      agentId: agentId,
      endpoint: endpoint,
      apiKey: apiKey,
      agentName: agentName
    };
    sessionStorage.setItem('pioagent_agent_context', JSON.stringify(agentInfo));
    navigate('/pioagent/chat');
  };

  return (
    <Card
      sx={{
        height: '100%',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-6px) scale(1.03)',
          boxShadow: theme.shadows[6],
        },
        position: 'relative',
        borderRadius: 4,
        boxShadow: theme.shadows[2],
        border: `1.5px solid ${theme.palette.divider}`,
        minHeight: 360,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        background: theme.palette.background.paper
      }}
    >
      <CardContent sx={{ p: 3, pb: '24px!important', position: 'relative' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700, 
              fontSize: '1.18rem', 
              color: theme.palette.text.primary, 
              maxWidth: agent.isAssigned ? '100%' : '70%', 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap' 
            }}
          >
            {agent.name}
          </Typography>
          {!agent.isAssigned && onDelete && (
            <IconButton
              size="small"
              color="error"
              onClick={handleDelete}
              sx={{ ml: 0.5 }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
          {agent.isAssigned && agent.assigned_role && (
            <Chip
              label={agent.assigned_role.toUpperCase()}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 22,
                borderRadius: 2,
                ml: 0.5,
                bgcolor: theme.palette.info.main,
                color: 'white'
              }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1, fontWeight: 500 }}>
            AI Agent
          </Typography>
          <Chip
            label={agent.isActive ? 'ACTIVE' : 'INACTIVE'}
            color={agent.isActive ? 'success' : 'warning'}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 22,
              borderRadius: 2,
              letterSpacing: 0.5,
              bgcolor: agent.isActive ? theme.palette.primary.main : theme.palette.warning.main,
              color: agent.isActive ? theme.palette.primary.contrastText : theme.palette.warning.contrastText,
              '& .MuiChip-label': {
                px: 1.2
              }
            }}
          />
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <FingerprintIcon fontSize="small" sx={{ mr: 0.5, color: theme.palette.primary.main, opacity: 0.8 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                Agent ID
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: theme.palette.text.primary, wordBreak: 'break-all' }}>
              {agent.id}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <LinkIcon fontSize="small" sx={{ mr: 0.5, color: theme.palette.primary.main, opacity: 0.8 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                Endpoint
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: theme.palette.text.primary, wordBreak: 'break-all' }}>
              {agent.endpoint}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccessTimeIcon fontSize="small" sx={{ mr: 0.5, color: theme.palette.primary.main, opacity: 0.8 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                Created
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
              {formatDate(agent.createdAt)}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccessTimeIcon fontSize="small" sx={{ mr: 0.5, color: theme.palette.primary.main, opacity: 0.8 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                Updated
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
              {formatDate(agent.updatedAt)}
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
          {!onlyServiceRoleUser && (
            <Button
              variant="contained"
              onClick={handleManageAgent}
              sx={{
                flex: 1,
                py: 1,
                px: 2,
                fontSize: '0.85rem',
                fontWeight: 600,
                borderRadius: 1.5,
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                textTransform: 'none',
                minWidth: 'auto',
                '&:hover': {
                  bgcolor: theme.palette.primary.dark,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Manage
            </Button>
          )}
          <Button
            variant={onlyServiceRoleUser ? "contained" : "outlined"}
            onClick={handleStartPioAgent}
            fullWidth={onlyServiceRoleUser}
            sx={{
              flex: onlyServiceRoleUser ? 1 : 1,
              py: 1,
              px: 2,
              fontSize: '0.85rem',
              fontWeight: 600,
              borderRadius: 1.5,
              borderWidth: onlyServiceRoleUser ? 0 : 1.5,
              borderColor: theme.palette.primary.main,
              color: onlyServiceRoleUser ? theme.palette.primary.contrastText : theme.palette.primary.main,
              bgcolor: onlyServiceRoleUser ? theme.palette.primary.main : 'transparent',
              textTransform: 'none',
              minWidth: 'auto',
              boxShadow: onlyServiceRoleUser ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
              '&:hover': {
                borderWidth: onlyServiceRoleUser ? 0 : 1.5,
                borderColor: theme.palette.primary.dark,
                bgcolor: onlyServiceRoleUser 
                  ? theme.palette.primary.dark 
                  : (theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.04)' 
                    : 'rgba(0, 0, 0, 0.02)'),
                boxShadow: onlyServiceRoleUser ? '0 2px 6px rgba(0,0,0,0.15)' : 'none',
              },
              transition: 'all 0.2s ease',
            }}
          >
            Start
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

AgentCard.propTypes = {
  agent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    endpoint: PropTypes.string.isRequired,
    isActive: PropTypes.bool.isRequired,
    createdAt: PropTypes.string.isRequired,
    updatedAt: PropTypes.string.isRequired
  }).isRequired,
  onDelete: PropTypes.func.isRequired
};

export default AgentCard; 